"use client";

import React from "react";
import { BrainCircuit, CalendarClock, Cpu, CircleCheck } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const features = [{
  title: "Ai Powered Technician",
  description: "We built a virtual service technician that is powered by Ai and trained on an extensive database of 45+ years of CNC service experience and \"tribal knowledge\".",
  icon: BrainCircuit,
  iconColor: "text-primary",
  glowClass: "hover:border-chart-1/50 hover:shadow-[0_0_40px_-10px_var(--color-chart-1)]",
  highlights: ["Instant troubleshooting help", "Reduce machine downtime", "Powered by 45+ years of expertise"]
}, {
  title: "Preventative Maintenance",
  description: "Track maintenance tasks that actually reduce unexpected breakdowns and downtime on your machines. Preventing downtime has never been easier.",
  icon: CalendarClock,
  iconColor: "text-secondary",
  glowClass: "hover:border-secondary/50 hover:shadow-[0_0_40px_-10px_var(--color-secondary)]",
  highlights: ["Track maintenance tasks", "Maintenance tasks curated by experts\n", "Extend machine lifespan"]
}, {
  title: "Component Repair",
  description: "Get heavy discounts on electronic component repair, bypassing expensive factory replacements.",
  icon: Cpu,
  iconColor: "text-destructive",
  glowClass: "hover:border-destructive/50 hover:shadow-[0_0_40px_-10px_var(--color-destructive)]",
  highlights: ["Significant cost savings", "Fanuc, HAAS, Mitsubishi, and more", "Reduce the burden of component failure"]
}];
export default function TheCncwiseEcosystem() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="the-cncwise-ecosystem" className="dark bg-background text-foreground py-24 px-6 md:px-12 lg:px-24 overflow-hidden relative">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/5 blur-[120px] rounded-lg pointer-events-none" />

      <div id="cncwise-ecosystem" ref={ref} className={`max-w-7xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground text-center">The CNCWise® Ecosystem</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">A comprehensive suite of tools designed to reduce downtime, reduce repair costs, and bring expert-level troubleshooting directly to your shop floor.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, i) => {
          const Icon = feature.icon;
          return <Card key={i} data-index={i} className={`bg-card/50 backdrop-blur-sm border-border/40 transition-all duration-500 hover:-translate-y-2.5 ${feature.glowClass} flex flex-col h-full`}>
                <CardHeader className="pb-4">
                  <div className={`w-14 h-14 mx-auto rounded-xl bg-background/80 border border-border/50 flex items-center justify-center mb-6 shadow-inner ${feature.iconColor}`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <CardTitle className={`text-2xl font-semibold tracking-tight ${i === 0 || i === 1 || i === 2 ? "text-center" : ""}`}>
                    {feature.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col gap-6">
                  <p className="text-muted-foreground leading-relaxed text-base">
                    {feature.description}
                  </p>
                  <ul className="mt-auto space-y-3 pt-6 border-t border-border/30">
                    {feature.highlights.map((highlight, j) => <li key={j} data-index={j} className="flex items-start gap-3">
                        <CircleCheck className={`w-5 h-5 shrink-0 mt-0.5 ${feature.iconColor}`} />
                        <span className="text-sm text-foreground/90 leading-relaxed">
                          {highlight}
                        </span>
                      </li>)}
                  </ul>
                </CardContent>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}